import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from './useAuth';

// Server-side deduplication: unique index `match_invitations_match_id_invitee_id_key`
// on (match_id, invitee_id) enforces uniqueness across all devices/sessions.
// Duplicate inserts return Postgres error code 23505, handled in onError below.



export function useMyUpcomingMatches() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['my-upcoming-matches', profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('matches')
        .select('*')
        .eq('created_by', profile!.id)
        .eq('status', 'upcoming')
        .gte('date', new Date().toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id,
  });
}

export function useMatchInvitations(matchId?: string) {
  return useQuery({
    queryKey: ['match-invitations', matchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('match_invitations')
        .select(`
          *,
          inviter:profiles!match_invitations_inviter_id_fkey(id, name, avatar, rank),
          invitee:profiles!match_invitations_invitee_id_fkey(id, name, avatar, rank),
          match:matches!match_invitations_match_id_fkey(id, sport, date, time, location, match_type)
        `)
        .eq('match_id', matchId!);

      if (error) throw error;
      return data;
    },
    enabled: !!matchId,
  });
}

export function useMyPendingInvitations() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['my-pending-invitations', profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('match_invitations')
        .select(`
          *,
          inviter:profiles!match_invitations_inviter_id_fkey(id, name, avatar, rank),
          match:matches!match_invitations_match_id_fkey(id, sport, date, time, location, match_type, status)
        `)
        .eq('invitee_id', profile!.id)
        .eq('status', 'pending');

      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id,
  });
}

export function useSendMatchInvitation() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      matchId,
      inviteeId,
      message,
      team,
    }: {
      matchId: string;
      inviteeId: string;
      message?: string;
      team?: number;
    }) => {
      // No client-side lock: dedup is enforced server-side by the unique index
      // on (match_id, invitee_id). Duplicate inserts fail with 23505.
      const { data, error } = await supabase
        .from('match_invitations')
        .insert({
          match_id: matchId,
          inviter_id: profile!.id,
          invitee_id: inviteeId,
          message,
          team,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Bạn đã mời người chơi này rồi');
        }
        throw error;
      }
      return data;

    },
    onSuccess: (_, { matchId }) => {
      queryClient.invalidateQueries({ queryKey: ['match-invitations', matchId] });
      toast.success('Đã gửi lời mời thành công');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Có lỗi xảy ra');
    },

  });
}

export function useRespondToInvitation() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      invitationId,
      status,
      matchId,
      team,
    }: {
      invitationId: string;
      status: 'accepted' | 'rejected';
      matchId: string;
      team?: number;
    }): Promise<{ status: string; newMatchId?: string; requiresPayment?: boolean; feeAmount?: number }> => {
      if (status === 'rejected') {
        const { error: updateError } = await supabase
          .from('match_invitations')
          .update({
            status,
            responded_at: new Date().toISOString(),
          })
          .eq('id', invitationId);
        if (updateError) throw updateError;
        return { status };
      }

      // status === 'accepted' — check match fee first
      const { data: matchData, error: matchError } = await supabase
        .from('matches')
        .select('*')
        .eq('id', matchId)
        .single();
      if (matchError) throw matchError;

      const { data: invitation, error: invitationError } = await supabase
        .from('match_invitations')
        .select('inviter_id')
        .eq('id', invitationId)
        .single();
      if (invitationError) throw invitationError;

      // BUG #15 FIX: Add ban check, schedule conflict check
      const { data: isBanned } = await supabase.rpc('is_player_banned', {
        _profile_id: profile!.id,
      });
      if (isBanned) {
        throw new Error('Bạn đang bị hạn chế tham gia trận đấu.');
      }

      const { data: hasConflict } = await supabase.rpc('check_schedule_conflict', {
        _profile_id: profile!.id,
        _match_date: matchData.date,
        _match_time: matchData.time,
      });
      if (hasConflict) {
        throw new Error('Bạn đã có lịch tham gia một trận đấu khác trong khoảng thời gian này.');
      }

      // BUG #14 FIX: Check daily ranked limit for ranked matches
      if (matchData.is_ranked) {
        const { data: limitCheck, error: limitError } = await supabase.rpc('check_daily_ranked_limit', {
          player_id: profile!.id,
          opponent_id: invitation.inviter_id,
        });

        if (limitError) throw limitError;

        if (limitCheck && limitCheck.length > 0 && !limitCheck[0].can_play) {
          throw new Error(limitCheck[0].reason || 'Đã đạt giới hạn trận rank hôm nay');
        }

        // Rank gate: always re-fetch BOTH requester + creator profile from DB
        // (never trust AuthContext cache — it can be stale after profile edits).
        // NOTE: RLS trên `profiles` chỉ cho đọc của chính user → creator phải
        // đọc qua view `profiles_public`. Dùng `.maybeSingle()` để an toàn.
        const [requesterRes, creatorRes] = await Promise.all([
          supabase
            .from('profiles')
            .select('skill_level, ranked_sport')
            .eq('id', profile!.id)
            .maybeSingle(),
          supabase
            .from('profiles_public')
            .select('skill_level')
            .eq('id', matchData.created_by)
            .maybeSingle(),
        ]);

        const mySkillLevel = requesterRes.data?.skill_level;
        const myRankedSport = requesterRes.data?.ranked_sport;
        const creatorSkillLevel = creatorRes.data?.skill_level;

        if (mySkillLevel && creatorSkillLevel && mySkillLevel !== creatorSkillLevel) {
          throw new Error(
            `Trận rank chỉ dành cho trình độ ${creatorSkillLevel}. Trình độ của bạn là ${mySkillLevel}.`
          );
        }

        if (myRankedSport && myRankedSport !== matchData.sport) {
          throw new Error(`Bạn chỉ có thể tham gia trận ranked với môn ${myRankedSport} đã đăng ký.`);
        }
      }


      const feeAmount = (matchData as any).recruiting_fee || (matchData as any).fee || 0;

      // Paid match (ranked hoặc không) → chỉ update invitation, chờ thanh toán escrow
      if (feeAmount > 0) {
        const matchStart = new Date(`${matchData.date}T${matchData.time}`);
        const paymentDeadline = new Date(matchStart.getTime() - 60 * 60 * 1000);

        const { error: updateError } = await supabase
          .from('match_invitations')
          .update({
            status: 'accepted',
            responded_at: new Date().toISOString(),
            payment_status: 'pending',
            payment_deadline: paymentDeadline.toISOString(),
          })
          .eq('id', invitationId);
        if (updateError) throw updateError;

        return { status, requiresPayment: true, feeAmount };
      }

      // Free match → add participant directly
      const { error: updateError } = await supabase
        .from('match_invitations')
        .update({
          status: 'accepted',
          responded_at: new Date().toISOString(),
        })
        .eq('id', invitationId);
      if (updateError) throw updateError;

      // BUG #8 FIX: Use safe_add_match_participant RPC for atomic capacity check
      const { error: participantError } = await supabase.rpc('safe_add_match_participant', {
        _match_id: matchId,
        _profile_id: profile!.id,
        _team: team || null,
      });
      if (participantError) {
        if (participantError.message?.includes('đã tham gia') || participantError.code === '23505') {
          throw new Error('Bạn đã tham gia trận đấu này rồi');
        }
        throw new Error(participantError.message);
      }

      return { status };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['my-pending-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['match-participants'] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      if (data.status === 'accepted') {
        if (data.requiresPayment) {
          toast.success('Đã chấp nhận lời mời. Vui lòng thanh toán để hoàn tất.');
        } else {
          toast.success('Đã chấp nhận lời mời');
        }
      } else {
        toast.success('Đã từ chối lời mời');
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Có lỗi xảy ra');
    },
  });
}
